import React, { useRef, useState } from "react";
import { useIntersectionObserver } from "../../hooks";
import { useLanguage } from "../../lib/LanguageContext/LanguageContext.tsx";
import { Skill } from "../../lib/Data/types.ts";
import styles from "./Skills.module.scss";
import SkillIcon from "./SkillIcon";

interface SkillsProps {
  skills: Skill[];
}

const Skills: React.FC<SkillsProps> = React.memo(({ skills }) => {
  const { t } = useLanguage();
  const sectionRef = useIntersectionObserver();
  const [expandedCategories, setExpandedCategories] = useState<number[]>([0]);
  const listRefs = useRef<(HTMLUListElement | null)[]>([]);

  // Removed expensive DOM manipulation

  const toggleCategory = React.useCallback((index: number) => {
    setExpandedCategories((prev) => {
      const isExpanding = !prev.includes(index);
      return isExpanding ? [...prev, index] : prev.filter((i) => i !== index);
    });
  }, []);

  // Removed expensive DOM queries and manipulation

  return (
    <section ref={sectionRef} className={styles.skills}>
      <h2 className="fade-up-section">{t("skills")}</h2>
      <div>
        {skills.map((skill, index) => (
          <div
            key={index}
            className={`${styles.skillCategory} ${expandedCategories.includes(index) ? styles.expanded : ""
              }`}
          >
            <button
              className={`${styles.skillHeader} fade-up-section`}
              onClick={() => toggleCategory(index)}
              aria-expanded={expandedCategories.includes(index)}
              aria-controls={`skill-list-${index}`}
              aria-label={`${t(skill.category)} - ${expandedCategories.includes(index)
                ? t("clickToCollapse")
                : t("clickToExpand")
                }`}
            >
              <h3>{t(skill.category)}</h3>
              <span className={styles.expandIcon} aria-hidden="true">
                {expandedCategories.includes(index) ? "−" : "+"}
              </span>
            </button>
            <ul
              id={`skill-list-${index}`}
              ref={(el) => { listRefs.current[index] = el; }}
              className={`${styles.skillsList} ${expandedCategories.includes(index) ? styles.expanded : ""
                }`}
              role="list"
            >
              {skill.items.map((item, itemIndex) => (
                <li key={itemIndex}>
                  <span className={styles.skillWithIcon}>
                    <SkillIcon
                      skillName={item}
                      size={16}
                      className={styles.skillIcon}
                    />
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
});

export default Skills;
